import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';
import { BookOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { StarButton } from '@/components/StarButton';
import { useAuthor } from '@/hooks/useAuthor';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { getTagValue } from '@/lib/nip54';
import {
  getPublicationRefs,
  publicationSummary,
  publicationTitle,
  publicationType,
  PUBLICATION_INDEX_KIND,
} from '@/lib/nkbip01';

interface PublicationCardProps {
  event: NostrEvent;
}

export function PublicationCard({ event }: PublicationCardProps) {
  const author = useAuthor(event.pubkey);
  const displayName = author.data?.metadata?.name ?? `${event.pubkey.slice(0, 8)}…`;
  const slug = getTagValue(event, 'd') ?? '';
  const npub = nip19.npubEncode(event.pubkey);
  const summary = publicationSummary(event);
  const image = sanitizeUrl(getTagValue(event, 'image'));
  const sectionCount = getPublicationRefs(event).length;

  return (
    <Card className="overflow-hidden transition-shadow hover:shadow-md">
      {image && (
        <Link to={`/p/${encodeURIComponent(slug)}/${npub}`} className="block">
          <img src={image} alt="" className="h-32 w-full object-cover" loading="lazy" />
        </Link>
      )}
      <CardHeader>
        <div className="mb-1 flex items-center gap-2">
          <Badge variant="secondary" className="capitalize">
            <BookOpen className="size-3" />
            {publicationType(event)}
          </Badge>
          {sectionCount > 0 && (
            <span className="text-xs text-muted-foreground">
              {sectionCount} {sectionCount === 1 ? 'section' : 'sections'}
            </span>
          )}
        </div>
        <CardTitle className="text-lg">
          <Link to={`/p/${encodeURIComponent(slug)}/${npub}`} className="hover:underline">
            {publicationTitle(event)}
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {summary && <p className="line-clamp-3 text-sm text-muted-foreground break-words">{summary}</p>}
        <div className="flex items-center justify-between gap-2">
          <Link
            to={`/${npub}`}
            className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <Avatar className="size-5">
              <AvatarImage src={author.data?.metadata?.picture} alt={displayName} />
              <AvatarFallback>{displayName.charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <span className="truncate">{displayName}</span>
          </Link>
          <StarButton
            event={event}
            addressPointer={`${PUBLICATION_INDEX_KIND}:${event.pubkey}:${slug}`}
            className="shrink-0"
          />
        </div>
      </CardContent>
    </Card>
  );
}
